/**
 * OffersOnly - a page that only exists while offers are switched on
 * -----------------------------------------------------------------
 * Wraps the offer pages in App.jsx. Offers were archived on 17 October 2026
 * (OFFERS_ENABLED in backend/config/features.js), but the pages are still
 * there, ready for the day they come back - and people still have old links
 * to them, in notifications, in emails and in their browser history.
 *
 * So rather than a "not found", somebody following one of those links is
 * taken somewhere useful:
 *
 *   <OffersOnly otherwise="/available-students"><Offers /></OffersOnly>
 *
 * The server refuses every offer route while the switch is off, so this is
 * about where a person lands, not about keeping anything out.
 */

import { Navigate } from 'react-router-dom';

import { useFeatures } from '../hooks/useFeatures';
import { useI18n } from '../hooks/useI18n';
import Loader from './Loader';

export default function OffersOnly({ children, otherwise }) {
  const { t } = useI18n();
  const { offersEnabled, ready } = useFeatures();

  // Wait for the real answer. Sending somebody away on the "off until we
  // know" default would bounce them out of a page that does exist.
  if (!ready) return <Loader label={t('common.loading')} />;

  if (!offersEnabled) return <Navigate to={otherwise} replace />;

  return children;
}
